import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Switch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { ACQUISITION_CARD_WIDTH, STEPPER_MAX_WIDTH } from '../../../theme';
import { Grid, Paper, Typography } from '@material-ui/core';
import AOIChooser from '../../components/mepbay/AOIChooser';
import PeriodImageChooser from '../../components/mepbay/PeriodImageChooser';
import WorkflowStepper from '../../components/steps/WorkflowStepper';
import StepSummary from '../../components/steps/StepSummary';
import Footer from '../../components/core/Footer';
import { getMepBayParameters } from '../../../selectors';
import { Actions as Auth } from '../../../store/ducks/auth';
import SatelliteChooser from '../../components/mepbay/SatelliteChooser';
import { FIRST, useStepNavigations } from '../../../hooks/useStepNavigations';

/**
 * Module for the mepbay process
 * @module Mepbay
 */

/**
 * @constant MODULE_ACCENT
 * @description Mepbay's identity color. Shared by the page header and the step indicator, so the
 * two read as one module instead of as two unrelated palettes.
 * @memberof module:Mepbay
 */
const MODULE_ACCENT = '#2b6fac';

const STEPS = [
  {
    label: 'forms.acquisition.1.title',
    content: 'forms.acquisition.1.description',
    component: SatelliteChooser,
  },
  {
    label: 'forms.acquisition.2.title',
    content: 'forms.acquisition.2.description',
    requires: 'satellite',
    component: AOIChooser,
  },
  {
    label: 'forms.mepbay.periodAndImages.title',
    content: 'forms.mepbay.periodAndImages.description',
    requires: 'geometry',
    component: PeriodImageChooser,
  },
];

/**
 * @function MepBayPage
 * @description Main component for the MEPBay acquisition process.
 * The acquisition process is divided into multiple steps, each represented by a separate component.
 * @param {Object} props - Component properties passed from the router (match, location, history)
 * @see {@link SatelliteChooser} - Component for selecting satellite
 * @see {@link AOIChooser} - Component for defining area of interest (AOI)
 * @see {@link PeriodImageChooser} - Component for selecting the period and refining the image list
 * **Called by:
 * @see {MainPage} - via the `/mepbay/:step?` Route.
 * @memberof module:Mepbay
 * @returns {JSX.Element} Complete acquisition page JSX element
 */
const MepBayPage = (props) => {
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const classes = useStyles();
  const { match } = props;
  const { acquisitionData, navigate, navigateTo, stepAttendsRequirements, canRevisitStep, makeRoutes } = useStepNavigations(
    match,
    STEPS,
    `/main/mepbay/result`,
    'mepbay',
    getMepBayParameters,
  );

  useEffect(() => {
    dispatch(Auth.loadClientAuth());
  }, []);

  useEffect(() => {
    dispatch(Auth.begin());
    if (!stepAttendsRequirements()) navigate(FIRST);
  }, [dispatch]);

  const hasStep = match.params.step !== undefined;
  let step = hasStep ? match.params.step - 1 : 0;
  if (step < 0 || step >= STEPS.length) step = 0;

  return (
    <div className={classes.wrapper}>
      <Grid container spacing={0} justifyContent="center">
        <Grid item xs={12} className={classes.header}>
          <div>
            <Typography className={classes.title} variant="h4" align="center">
              {t('forms.mepbay.title')}
            </Typography>
          </div>
        </Grid>
        <Grid item className={classes.container}>
          <Paper className={classes.content} elevation={1}>
            <WorkflowStepper
              className={classes.stepper}
              accentColor={MODULE_ACCENT}
              steps={STEPS.map((el, i) => ({ id: String(i), label: t(el.label), disabled: !canRevisitStep(i) }))}
              currentStep={String(step)}
              onStepClick={(i) => canRevisitStep(i) && navigateTo(i)}
            />
            <StepSummary acquisition={acquisitionData} className={classes.summary} />
            {STEPS[step]?.content && (
              <Typography className={classes.instructions} align="center">
                {t(STEPS[step].content)}
              </Typography>
            )}
            <Switch>{makeRoutes(hasStep)}</Switch>
          </Paper>
        </Grid>
      </Grid>
      <Footer />
    </div>
  );
};

/**
 * @function useStyles
 * @description Hook for styling using Material-UI's makeStyles. Defines CSS styles for the
 * MEPBay acquisition wizard.
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @memberof module:Mepbay
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles((theme) => ({
  wrapper: {
    display: 'flex',
    flexFlow: 'column',
    flexGrow: 1,
  },
  header: {
    backgroundColor: MODULE_ACCENT,
    padding: theme.spacing(4, 0, 12),
  },
  title: {
    color: 'white',
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    flexFlow: 'column',
  },
  content: {
    width: ACQUISITION_CARD_WIDTH,
    marginTop: theme.spacing(-9),
  },
  summary: {
    marginBottom: theme.spacing(1),
  },
  instructions: {
    paddingTop: theme.spacing(1),
    marginBottom: theme.spacing(3),
  },
  /**
   * The wizard header overlaps the card above it, so the indicator needs its own top room; the
   * max-width keeps the connectors short and centred instead of running the full card width.
   */
  stepper: {
    maxWidth: STEPPER_MAX_WIDTH,
    padding: theme.spacing(4, 3, 1.5),
  },
}));

export default MepBayPage;
